import { SpecialistLevels } from "./Subscription";
import { TypeUserData, UserSchedule } from "./TypeUsers";

export type Label = {
  text: string;
  language: string;
};

export type LocalizedTextList = {
  language: string;
  items: string[];
};

export type ProviderEvidenceTrainingType =
  | "basic"
  | "specialization"
  | "continuing";

export type ProviderEvidenceFile = {
  filepath: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  url?: string;
};

export type ProviderWorkExperience = {
  companyName: Label[];
  jobTitle: Label[];
  mainFunctions: LocalizedTextList[];
  startDate: string;
  endDate?: string;
  subSpecializations?: string[];
};

export type ProviderProfessionalAttributes = {
  professionalStatus?: string[];
  primaryJurisdictionCountry?: string;
  primaryJurisdictionDetails?: string;
  targetAudience?: string[];
  pharmacotherapy?: boolean;
  clinicalDiagnostics?: string[];
  academicDegree?: string;
  totalProviderHours?: number;
  hoursBySubSpecialization?: { subSpecialization: string; hours: number }[];
  casesBySubSpecialization?: { subSpecialization: string; cases: number }[];
  methodology?: {
    subSpecialization?: string;
    description?: Label[];
    text?: Label[];
  }[];
  industryFocus?: LocalizedTextList[];
};

export type ProviderCredentialEvidence = {
  credentialType: string;
  identifier?: string;
  issueDate?: string;
  expirationDate?: string;
  issuingOrganization?: string;
  jurisdictionCountry?: string;
  jurisdictionDetails?: string;
  proofFile?: ProviderEvidenceFile;
};

export type UploadIntroVideoPayload = {
  file: FormData;
  specialistId: string;
};
export type UploadCertificatesPayload = {
  files: FormData;
  specialistId: string;
};
export type SubSpecializations = {
  _id: string;
  specialization: string;
  relatedMainSpec: MainSpecializations[];
  labels: { text: string; language: string }[];
};

export type MainSpecializations = {
  _id: string;
  specialization: string;
  labels: { text: string; language: string }[];
};

export type VideoMetadata = {
  _id: string;
  length: number;
  chunkSize: number;
  uploadDate: string;
  filename: string;
};

export type SpecialistLevel = {
  _id: string;
  title: { text: string; language: string }[];
  description: { text: string; language: string }[];
  mainSpecialization: MainSpecializations;
  level: string;
  tier: number;
};

export type ServicesConfig = {
  _id: string;
  showInCatalog: { switch: boolean; allowed: boolean };
  showInTreeQuiz: { switch: boolean; allowed: boolean };
  showInSubscription: { switch: boolean; allowed: boolean };
  showInProjects: { switch: boolean; allowed: boolean };
  showInBarter: { switch: boolean; allowed: boolean };
  showInPackageRecommend: { switch: boolean; allowed: boolean };
  reviewRequested: boolean;
};

export enum DiscountTypes {
  SUBSCRIPTION = "SUBSCRIPTION",
  SELECTION = "SELECTION",
  PROJECT = "PROJECT",
}

export type SpecialistData = {
  _id: string;
  mainSpecializations: MainSpecializations[];
  subSpecializations: SubSpecializations[];
  receiveRealTimeOffers: boolean;
  preferredLanguages?: string[];
  practiceHours?: number;
  yearsOfExperience?: number;
  introductionVideo?: {
    video?: VideoMetadata;
    videoS3?: string;
    language: string;
    processing?: boolean;
  }[];
  certificates?: string[];
  educationCertificates: string[];
  federationCertificates: string[];
  qualificationCertificates: string[];
  appreciationCertificates: string[];
  aboutMyself?: { text: string; language: string }[];
  aboutTherapy?: { text: string; language: string }[];
  contributeToProfession?: { text: string; language: string }[];
  price?: { amount: number; language: string }[];
  clients?: string[];
  totalRating?: number;
  rating: number;
  isVerified: boolean;
  isVerifiedChanges: boolean;
  discountPrice?: number;
  tags: SpecialistTag[];
  servicesConfig: ServicesConfig;
  taxCategory?: string | undefined;
  level?: {
    _id: string;
    title: { text: string; language: string }[];
    description: { text: string; language: string }[];
    level: SpecialistLevels;
    mainSpecialization: string;
  };
  showInSubscriptions?: boolean;
  completedCourses?: number;
  federationMemberships?: number;
  qualificationsFromFederations?: number;
  schedule?: UserSchedule;
  bufferTime?: {
    before: number;
    after: number;
  };
  minimumBookingsGap: number;
  bookingTimeStep: number;
  reviewRequested?: boolean;
  practiceStartYear?: string;
  sessionCount?: number;
  workExperience?: ProviderWorkExperience[];
  providerCredentials?: ProviderCredentialEvidence[];
  providerProfessionalAttributes?: ProviderProfessionalAttributes;
  moderatorOnlyProviderEvidence?: Record<
    string,
    unknown | ProviderEvidenceFile
  >;
};
export type OverLaps = {
  specialist: string;
  overlaps: {
    day: string;
    overlapTime: {
      start: Date;
      end: Date;
    }[];
  }[];
};
export type Certificates = {
  _id: string;
  filename: string;
  size: number;
  type: string;
  link: string;
};

type Range = {
  min: number;
  max: number;
};
export type ParamsAvailable = {
  genders: string[];
  locations: string[];
  totalAmount: number;
  preferredLanguages: string[];
  mainSpecializations: string[];
  subSpecializations: string[];
  priceRange: Range;
  ageRange: Range;
  practiceHoursRange: Range;
  yearsOfExperienceRange: Range;
  tags: { _id: string; direction: string; title: string; labels: Label[] }[];
};

export type TypeChoosableParamsAvailable = {
  genders?: { gender: string; choosable: boolean }[];
  locations?: { location: string; choosable: boolean }[];
  preferredLanguages?: { language: string; choosable: boolean }[];
  mainSpecializations?: {
    mainSpecialization: MainSpecializations;
    choosable: boolean;
  }[];
  subSpecializations?: {
    subSpecialization: SubSpecializations;
    choosable: boolean;
  }[];
  priceRange?: { min: number; max: number };
  ageRange: { min: number; max: number };
  practiceHoursRange: { min: number; max: number };
  yearsOfExperienceRange: { min: number; max: number };
  totalAmount: number;
  levels: {
    minPrice: number;
    maxPrice: number;
    levels: {
      level: SpecialistLevel & { avgPrice: number };
      choosable: boolean;
    }[];
  };
  tags: {
    tag: SpecialistTag;
    choosable: boolean;
  }[];
};

export type TypeSpecialistFilter = {
  gender?: string[];
  age?: { from: number; to: number };
  preferredLanguages?: string[];
  specialistId?: {
    subSpecializations?: string[];
    mainSpecializations?: string[];
    practiceHours?: { from: number; to: number };
    yearsOfExperience?: { from: number; to: number };
    price?: { from: number; to: number };
    isVerified?: boolean;
    level?: string[];
    tags?: string[];
    showInCatalog?: boolean;
    isVerifiedChanges?: boolean;
    reviewRequested?: boolean;
    servicesConfig?: {
      reviewRequested?: boolean;
      showInSubscription?: { switch?: boolean; allowed?: boolean };
    };
  };
  preferredTime?: string[];
  page?: number;
  limit?: number;
  daysAmountToConsider?: number;
  name?: string;
  email?: string;
  count?: boolean;
  country?: string[];
  city?: string[];
  priceRange?: boolean;
  receiveRealTimeOffers?: boolean;
  considerOverlaps?: boolean;
  sort?: {
    field: string;
    type: -1 | 1;
  };
  time?: string;
  IsFavourite?: boolean;
  isBanned?: boolean;
};

export interface TimeRange {
  start: Date;
  end: Date;
}

export type SpecialistFreeTimeType = {
  specialist: string;
  overlaps: { day: string; overlapTime: TimeRange[] }[];
};

export type RealTimeBooking = {
  from: TypeUserData;
  for: TypeUserData;
  isAccepted: boolean;
};
export type Specializations = {
  specialization: string;
  labels: { text: string; language: string }[];
};

export enum SpecialistTagsDirections {
  COACHING = "Coaching",
  PSYCHOTHERAPY = "Psychotherapy",
  PSYCHOLOGY = "Psychology",
  PSYCHIATRY = "Psychiatry",
  LAWYER = "Lawyer",
  NUTRITIONIST = "Nutritionist",
  DIETITIAN = "Dietitian",
  MEDIATOR = "Mediator",
  MENTOR = "Mentor",
  CONSULTING = "Consulting",
}

export type SpecialistTag = {
  _id: string;
  title: string;
  labels: { text: string; language: string }[];
  parentTag?: string;
  level: number;
  direction: SpecialistTagsDirections;
};

export type Federation = {
  _id: string;
  text: { text: string; language: string }[];
  isShown: boolean;
  specialization: MainSpecializations;
  badge?: {
    file?: string | StoredFileView;
    fileS3?: string;
    url: string;
    _id: string;
    link?: string;
  };
  deleted: boolean;
};
export type AppreciationCertificate = {
  _id: string;
  file?: {
    _id: string;
    filename?: string;
    size?: number;
    type?: string;
    link?: string;
  };
  fileS3?: string;
  originalFile?: string;
  originalFileS3?: string;
  filename?: string;
  size?: number;
  type?: string;
  link?: string;
  receiveDate: string;
  organization: string;
  receivedFor: string;
};
export type Education = {
  _id: string;
  text: { language: string; text: string }[];
  isShown: boolean;
  specialization: MainSpecializations;
  deleted: boolean;
};
export type EducationCourse = {
  _id: string;
  text: { language: string; text: string }[];
  isShown: boolean;
  education: Education;
  deleted: boolean;
};
export type EducationAccreditation = {
  _id: string;
  text: { language: string; text: string }[];
  badge?: Badge;
  isShown: boolean;
  educationCourse: EducationCourse;
  deleted: boolean;
};
type MultiLangTextObject = { text: string; language: string };
export type Qualification = {
  _id: string;
  text: { text: string; language: string }[];
  isShown: boolean;
  specialization: MainSpecializations;
  badge?: {
    file?: string | StoredFileView;
    fileS3?: string;
    url: string;
    _id: string;
    link?: string;
  };
  federationRef: Federation;
  deleted: boolean;
};

export type StoredFileView = {
  _id?: string;
  filepath?: string;
  filename?: string;
  type?: string;
  size?: number;
  link?: string;
};

export type Badge = {
  _id: string;
  file: string | StoredFileView;
  fileS3?: string;
  description?: string;
  url: string;
  isShown: boolean;
  specialization: MainSpecializations;
  link: string;
  title: MultiLangTextObject[];
};

export type FederationsCertificatesResponse = {
  _id: string;
  filename?: string;
  fileS3?: string;
  originalFile?: string;
  originalFileS3?: string;
  federationProfileUrl: string;
  size?: number;
  type?: string;
  link?: string;
  membershipEnd: string;
  membershipStart: string;
  certificateNumber?: string;
  federation: Omit<Federation, "badge"> & {
    badge: Badge & {
      _id: string;
      filename: string;
      size: number;
      type: string;
      link: string;
    };
  };
};

export type EducationCertificatesResponse = {
  _id: string;
  filename?: string;
  fileS3?: string;
  originalFile?: string;
  originalFileS3?: string;
  size?: number;
  type?: string;
  link?: string;
  membershipEnd?: string;
  membershipStart?: string;
  description: string;
  education: Education;
  studyHours: number;
  subSpecializations: SubSpecializations[];
  trainingType?: ProviderEvidenceTrainingType;
  documentNumber?: string;
  educationCourse: {
    _id: string;
    isShown: boolean;
    text: { language: string; text: string }[];
  };
  educationAccreditation?: {
    _id: string;
    isShown: boolean;
    text: { language: string; text: string }[];
  }[];
  finished?: boolean;
};

export type QualificationCertificatesResponse = {
  _id: string;
  filename?: string;
  fileS3?: string;
  originalFile?: string;
  originalFileS3?: string;
  federationProfileUrl: string;
  size?: number;
  type?: string;
  link?: string;
  membershipEnd: string;
  membershipStart: string;
  certificateNumber?: string;
  subSpecializations?: SubSpecializations[];
  qualification: Omit<Qualification, "badge"> & {
    badge: Badge & {
      _id: string;
      filename: string;
      size: number;
      type: string;
      link: string;
    };
  };
};

export type FieldUpdate<T> = {
  old: T;
  new: T;
};

export type UserUpdates = {
  [K in keyof Partial<Omit<TypeUserData, "specialistIds">>]: FieldUpdate<
    Partial<TypeUserData>[K]
  >;
} & {
  ["specialistIds"]: ({
    [K in keyof Partial<Omit<SpecialistData, "_id">>]: FieldUpdate<
      Partial<SpecialistData>[K]
    >;
  } & { ["_id"]: string })[];
};

export type TaxCategory = {
  _id: string;
  title: { text: string; language: string }[];
  description?: { text: string; language: string }[];
  sessionTaxPercent: number;
  packageTaxPercent: number;
  mainSpecialization: MainSpecializations;
};

export type NewCategory = {
  title: { text: string; language: string }[];
  description?: { text: string; language: string }[];
  sessionTaxPercent: number;
  packageTaxPercent: number;
  mainSpecialization: string;
};
export type VerifiedSpecsStats = {
  totalSpecialists: number;
  uniqueLanguagesCount: number;
  averageSessionPrice: number;
  uniqueCountriesCount: number;
  totalCoaches: number;
  totalPsychotherapists: number;
  coachAvatarLinks: string[];
  psychotherapistAvatarLinks: string[];
};

export type ProviderEvidenceBlock =
  | "educationCertificates"
  | "qualificationCertificates"
  | "federationCertificates"
  | "workExperience"
  | "providerCredentials"
  | "providerProfessionalAttributes"
  | "moderatorOnlyProviderEvidence";

export type ProviderEvidenceFailureReason =
  | "required"
  | "invalid"
  | "invalidReference"
  | "invalidOption"
  | "invalidCountry"
  | "tooLong"
  | "tooShort"
  | "unsupported";

export type ProviderEvidenceFillnessField = {
  block: ProviderEvidenceBlock;
  field: string;
  reason: ProviderEvidenceFailureReason;
  index?: number;
  language?: string;
};

export type ProfileFillPercentageResponse = {
  percentage?: number;
  passedFields?: string[];
  incorrectFields?: string[];
  incorrectLanguageFields?: { language: string; field: string }[];
  incorrectProviderEvidenceFields?: ProviderEvidenceFillnessField[];
};
